import React from 'react';
import DataGrid from '../Table/newTable';
import { projectionsHeaderConfig } from '../Table/newTableConfig';
import { Projection } from './types';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import Footer from './footer';
import ProjectionModal from './ProjectionModal';
import { Stack, Switch, FormControlLabel } from '@mui/material';
import { SlateResponse } from '../store/types';
import { useDispatch, useSelector } from 'react-redux';
import { setBoostedTeamsAction } from '../store/actions';


interface OwnProps {
  readonly disableCustomization?: boolean;
  readonly onSetProjections: (projections: Projection[]) => void;
  readonly disabled: boolean;
  readonly tableHeight?: string;
  readonly selectedSlate: SlateResponse;
  readonly emptyText?: string;
  readonly backgroundColor?: string;
  readonly useLiveProjections?: boolean;
  readonly setUseLiveProjections?: (useLive: boolean) => void;
  readonly simType?: string;
}

type Props = OwnProps;

const EasyProjection = (props: Props) => {
  const { selectedSlate, disabled, disableCustomization, emptyText, backgroundColor, tableHeight, onSetProjections, setUseLiveProjections, useLiveProjections, simType = 'pre' } = props;
  const [projections, setProjections] = React.useState<Projection[]>([]);
  const [originalProjections, setOriginalProjections] = React.useState<Projection[]>([]);
  const [customProjections, setCustomProjections] = React.useState<Projection[]>([]); //React.useState<Projection[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const { boostedTeams, selectedSite, selectedSport, removedTeams: removedTeamsRedux } = useSelector((state: any) => state.common);
  const dispatch = useDispatch();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const onSetProj = (newProjections: Projection[]) => {
    // Guard against undefined slateId
    if (!selectedSlate?.slateId) {
      return;
    }

    const projectionChanges = newProjections.filter((projection: Projection) => {
      const originalProjection = originalProjections.find((originalProjection: Projection) => originalProjection.nameAndId === projection.nameAndId);
      if (originalProjection) {
        return originalProjection.projection !== projection.projection;
      }
      return false;
    })

    sessionStorage.setItem(`projections-${selectedSlate.slateId}`, JSON.stringify(projectionChanges));
    setCustomProjections(newProjections);
  }

  const Buttons = () => {
    // Don't render if selectedSlate.slateId is undefined
    if (!selectedSlate?.slateId) {
      return null;
    }

    return (
      <Stack direction="row" gap="16px">
        {setUseLiveProjections && (
          <FormControlLabel
            control={
              <Switch
                checked={useLiveProjections}
                onChange={(e) => setUseLiveProjections(e.target.checked)}
                name="useLiveProjections"
                color="primary"
              />
            }
            label="Use Live Projections"
          />
        )}
        <Stack direction="row" gap="2px" alignItems="center" style={{ cursor: 'pointer', }} onClick={() => setProj(true)}>Reset <RefreshIcon fontSize='small' /></Stack>
        <ProjectionModal slateId={selectedSlate.slateId.toString()} selectedSlate={selectedSlate} onSetProj={(customProjections: Projection[]) => onSetProj(customProjections)} />
      </Stack>
    )
  }

  const setProj = async (resetProjections: boolean) => {
    // Guard against undefined slateId
    if (!selectedSlate?.slateId) {
      return;
    }

    setIsLoading(true);
    const currentProjections = await fetch(`${process.env.REACT_APP_API_URL}/api/slatedata/projections?SlateId=${selectedSlate.slateId}`).then(res => res.json())
    if (resetProjections) {
      sessionStorage.removeItem(`projections-${selectedSlate.slateId}`);
      setCustomProjections([]);
    }

    setOriginalProjections([]);
    const sessionProjections = sessionStorage.getItem(`projections-${selectedSlate.slateId}`);
    if (sessionProjections) {
      let newProjections = JSON.parse(JSON.stringify(currentProjections));
      for (let i = 0; i < newProjections.length; i++) {
        const sessionProjection = JSON.parse(sessionProjections).find((projection: Projection) => projection.nameAndId === newProjections[i].nameAndId);
        if (sessionProjection) {
          newProjections[i] = sessionProjection;
        }
      }
      setCustomProjections(newProjections);
    }
    else {
      setCustomProjections([]);
    }
    setIsLoading(false);
    setOriginalProjections(currentProjections)
  }

  React.useEffect(() => {
    if (customProjections?.length > 0) {
      setProjections(customProjections);
    } else {
      setProjections(originalProjections);
    }
  }, [customProjections, originalProjections])

  React.useEffect(() => {
    if (disabled) {
      setProjections([]);
    }
    if (projections !== undefined && !disabled && selectedSlate?.slateId !== undefined) {
      setProj(false);
    }
    else {
      setProjections([]);
      setOriginalProjections([]);
    }
  }, [selectedSlate.slateId, disabled])

  const updateSessionStorage = (newProjections: Projection[]) => {
    // Guard against undefined slateId
    if (!selectedSlate?.slateId) {
      return;
    }

    const projectionChanges = newProjections.filter((projection: Projection) => {
      const originalProjection = originalProjections.find((originalProjection: Projection) => originalProjection.nameAndId === projection.nameAndId);
      if (originalProjection) {
        return (originalProjection.projection !== projection.projection
          || originalProjection.variance !== projection.variance
        );
      }
      return false;
    })
    sessionStorage.setItem(`projections-${selectedSlate.slateId}`, JSON.stringify(projectionChanges));
  }

  React.useEffect(() => {
    const _projections = customProjections?.length ? customProjections : originalProjections;
    if (Object.keys(boostedTeams).length > 0) {
      let teamBoostedProjections = _projections.map(projection => Object.keys(boostedTeams).includes(projection.team) && !(selectedSport.sport?.toLowerCase() === 'mlb' && projection.position?.toLowerCase().includes('p')) ? { ...projection, projection: projection.projection * (1 + boostedTeams[projection.team]) } : projection)
      if (removedTeamsRedux.length > 0) {
        teamBoostedProjections = teamBoostedProjections.map(projection => removedTeamsRedux.includes(projection.team) ? { ...projection, projection: 0 } : projection)
      }
      setProjections(teamBoostedProjections)
    }
  }, [boostedTeams, removedTeamsRedux])

  React.useEffect(() => {
    // Guard against undefined slateId, site, or sport
    if (!selectedSlate?.slateId || !selectedSite?.site || !selectedSport?.sport) {
      return;
    }

    const savedTeamControl = localStorage.getItem(`easyProjectionGameControls-${selectedSite.site}-${selectedSport.sport}-${selectedSlate.slateId}`);
    if (savedTeamControl) {
      dispatch(setBoostedTeamsAction(JSON.parse(savedTeamControl)));
    } else {
      dispatch(setBoostedTeamsAction({}));
    }
  }, [originalProjections])

  return (
    <Box height={tableHeight || '100%'}>
      <DataGrid
        includeStacks={false}
        lineups={disableCustomization ? originalProjections : projections}
        headers={projectionsHeaderConfig(originalProjections, projections, updateSessionStorage, selectedSlate, disableCustomization || false, props.useLiveProjections, isMobile)}
        isLoading={isLoading}
        selectedView="projections"
        simType={simType}
        setStackView={() => {}}
        tierlocked={false}
        setPlayerList={() => {}}
        setStackList={() => {}}
        pagination
        rowHeight={isMobile ? 52 : 40}
        emptyText={emptyText || "Upload your lineups to start generating."}
        fontSize={isMobile ? 12 : 14}
        footer={() => disableCustomization ? <></> : <Footer onSetProjections={onSetProjections}
          disabled={disabled} />}
        projectionModal={() => disableCustomization ? <></> : <Buttons />}
        backgroundColor={backgroundColor || 'rgba(1, 90, 206, 0.15)'}
        isMobile={isMobile}
        pinnedColumnsLeft={isMobile ? ['name'] : undefined}
      />
    </Box>
  )
}

export default EasyProjection;